import { CalendarMonth } from "@mui/icons-material";
import {
  Box,
  Card,
  CardContent,
  CircularProgress,
  Divider,
  Grid,
  Stack,
  Typography,
} from "@mui/material";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import PagesContainer from "../../../components/UI/PagesContainer";
import useFetch from "../../../hooks/useFetch";
import { IDoctorAgenda } from "../../../models/doctor-agenda";
import DoctorAgendaService from "../../../services/doctorAgenda.service";
import NoData from "../../../components/UI/NoData";
import { DetailSection } from "./DetailSection";

type IParms = {
  identifier: string;
  appointmentDate: string;
} & Record<string, string>;

const translation_path = "page.doctorAgenda.appointmentDetails.";

const doctorAgendaService = new DoctorAgendaService();

const formatTimeUntil = (targetDate: Date, t: (key: string) => string) => {
  const now = new Date();
  const differenceInMs = targetDate.getTime() - now.getTime();

  if (differenceInMs <= 0) {
    return t(translation_path + "expired");
  }

  const minutes = Math.floor(differenceInMs / (1000 * 60));
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);
  const weeks = Math.floor(days / 7);

  const countdownIn = t(translation_path + "in");

  if (weeks > 0) {
    const unit =
      weeks > 1 ? t(translation_path + "weeks") : t(translation_path + "week");
    return `${countdownIn} ${weeks} ${unit}`;
  }
  if (days > 0) {
    const unit =
      days > 1 ? t(translation_path + "days") : t(translation_path + "day");
    return `${countdownIn} ${days} ${unit}`;
  }
  if (hours > 0) {
    return `${countdownIn} ${hours}h`;
  }
  return `${countdownIn} ${minutes} min`;
};

export const AppointmentDetails = () => {
  const { t, i18n } = useTranslation();
  const { identifier, appointmentDate } = useParams<IParms>();
  const { sendRequest, loading } = useFetch(
    doctorAgendaService.getByIdentifier
  );
  const [appointment, setAppointment] = useState<IDoctorAgenda | null>(null);

  const fetchAppointmentDetails = useCallback(async () => {
    if (identifier) {
      const { data, success } = await sendRequest({
        CalendarDateEnd: appointmentDate!,
        CalendarDateStart: appointmentDate!,
        identifier,
      });
      if (success && data) {
        setAppointment(data[0]);
      }
    }
  }, [identifier, appointmentDate, sendRequest]);

  useEffect(() => {
    fetchAppointmentDetails();
  }, [fetchAppointmentDetails]);

  const appointmentDateTime = useMemo(
    () => (appointment ? new Date(appointment.calendarStartDate) : new Date()),
    [appointment]
  );

  const durationInMinutes = useMemo(() => {
    if (!appointment) return 0;
    const start = new Date(appointment.calendarStartDate);
    const end = new Date(appointment.calendarEndDate);
    const timeDifference = end.getTime() - start.getTime();
    return Math.floor(timeDifference / (1000 * 60));
  }, [appointment]);

  const countdownText = useMemo(
    () => formatTimeUntil(appointmentDateTime, t),
    [appointmentDateTime, t]
  );

  const formattedDate = useMemo(
    () =>
      appointmentDateTime.toLocaleString(i18n.language, {
        day: "2-digit",
        month: "long",
        hour: "2-digit",
        minute: "2-digit",
      }),
    [appointmentDateTime, i18n.language]
  );

  if (loading) {
    return (
      <PagesContainer title={t(translation_path + "appointment_details")}>
        <Box display="flex" justifyContent="center" py={5}>
          <CircularProgress />
        </Box>
      </PagesContainer>
    );
  }

  if (!appointment) {
    return (
      <PagesContainer title={t(translation_path + "appointment_details")}>
        <Box
          display="flex"
          height="100%"
          justifyContent="center"
          alignItems="center"
          py={5}
        >
          <NoData />
        </Box>
      </PagesContainer>
    );
  }

  return (
    <PagesContainer title={t(translation_path + "appointment_details")}>
      <Box sx={{ pb: 4 }}>
        <Stack divider={<Divider flexItem />} spacing={0}>
          <DetailSection
            label={t(translation_path + "agenda_label")}
            value={appointment.descriptionSite}
          />
          <DetailSection
            label={t(translation_path + "type_label")}
            value={appointment.calendarClassificationName}
          />
          <DetailSection
            label={t(translation_path + "insurance_label")}
            value={appointment.insuranceName}
          />
          <DetailSection
            label={t(translation_path + "patient_label")}
            value={appointment.pacientName}
          />
          <Grid container spacing={2}>
            <DetailSection
              label={t(translation_path + "birth_date_label")}
              value={
                appointment.pacientBirthDate &&
                new Date(appointment.pacientBirthDate).toLocaleDateString(
                  "pt-BR"
                )
              }
              isHalfSize
            />
            <DetailSection
              label={t(translation_path + "age_label")}
              value={appointment.pacientAge}
              isHalfSize
            />
          </Grid>
          {appointment.calendarClassificationName && (
            <DetailSection
              label={t(translation_path + "classification_label")}
              value={appointment.calendarClassificationName}
            />
          )}
          {appointment.procedureName &&
            (appointment.tasyScheduleTypeCode === 2 ||
              appointment.tasyScheduleTypeCode === 1) && (
              <DetailSection
                label={t(translation_path + "procedure_label")}
                value={appointment.procedureName}
              />
            )}
          <DetailSection
            label={t(translation_path + "status_label")}
            value={appointment.calendarStatusName}
          />
        </Stack>

        <Card
          sx={{
            mt: 4,
            backgroundColor: "primary.main",
            color: "primary.contrastText",
            borderRadius: 3,
          }}
        >
          <CardContent>
            <Stack spacing={1}>
              <Stack direction="row" spacing={1} alignItems="center">
                <CalendarMonth />
                <Typography variant="h6" fontWeight="600">
                  {t(translation_path + "scheduling")}
                </Typography>
              </Stack>
              <Typography variant="body1">{formattedDate}</Typography>
              <Typography variant="body1">{`${durationInMinutes} min`}</Typography>
              <Typography variant="body2" sx={{ opacity: 0.9 }}>
                {countdownText}
              </Typography>
            </Stack>
          </CardContent>
        </Card>
      </Box>
    </PagesContainer>
  );
};
